import type { BimWorkOrderChangePriceData } from 'gql/graphql';
import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import { BimWorkOrderRowDetailsType } from 'pages/Project/BimProject/WorkOrder/types';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

import { BimWorkOrderChangeJustificationEdit } from '../../BimWorkOrderChangeJustificationEdit';

type TWorkOrderChangePriceContextMenu = {
    data: BimWorkOrderChangePriceData;
    openDetails: (detailsType: BimWorkOrderRowDetailsType) => void;
    refreshTable: () => void;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
};

export const WorkOrderChangePriceContextMenu = ({
    data,
    openDetails,
    refreshTable,
    workOrderChangeStatus,
}: TWorkOrderChangePriceContextMenu) => {
    const buttonRef = useRef(null);

    const [isContextOpen, setIsContextOpen] = useState<boolean>(false);
    const [isJustificationOpen, setIsJustificationOpen] = useState<boolean>(false);

    const toggleJustificationModal = useCallback(() => {
        setIsJustificationOpen(R.not);
    }, []);

    const kebabMenuItems = useMemo(
        () => [
            ...(data?.isAddedRow
                ? [
                      {
                          label: 'Обоснование сметы',
                          action: toggleJustificationModal,
                          disabled: workOrderChangeStatus !== WorkOrderChangeStatusEnum.OnFormation,
                      },
                  ]
                : []),
            ...(data?.hasRevisions
                ? [
                      {
                          label: 'Просмотреть Ревизии сметы',
                          action: () => {
                              openDetails(BimWorkOrderRowDetailsType.Revision);
                          },
                      },
                  ]
                : []),
            ...(data?.hasUpdates
                ? [
                      {
                          label: 'Просмотреть обновления по смете',
                          action: () => {
                              openDetails(BimWorkOrderRowDetailsType.Update);
                          },
                      },
                  ]
                : []),
        ],
        [
            data?.hasRevisions,
            data?.hasUpdates,
            data?.isAddedRow,
            openDetails,
            toggleJustificationModal,
            workOrderChangeStatus,
        ],
    );

    const onContextMenuButtonClick = useCallback((evt) => {
        evt.stopPropagation();
        setIsContextOpen(R.not);
    }, []);

    const isEmptyMenu = R.isEmpty(kebabMenuItems);

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                ref={buttonRef}
                onClick={onContextMenuButtonClick}
                disabled={isEmptyMenu}
            />
            <ContextMenu
                isOpen={isContextOpen}
                items={kebabMenuItems}
                anchorRef={buttonRef}
                direction="downStartLeft"
                onClickOutside={onContextMenuButtonClick}
                getItemOnClick={({ action }) => action}
            />
            {isJustificationOpen && (
                <BimWorkOrderChangeJustificationEdit
                    onClose={toggleJustificationModal}
                    onSuccess={refreshTable}
                    defaultValue={data?.rationale}
                    workOrderChangePriceId={data?.workOrderChangePriceId}
                />
            )}
        </>
    );
};

WorkOrderChangePriceContextMenu.displayName = 'WorkOrderChangePriceContextMenu';
